import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail } from 'lucide-react';

// Light-themed footer for the homepage. Mirrors the canonical dark
// Footer (src/components/layout/Footer.tsx) link-for-link so destinations
// stay consistent across the site, but styled for the homepage's light
// palette (white bg, gray text, #17A24A brand accent).
//
// Contact is a real mailto on the owned domain. Privacy/Terms route to the
// same contact inbox as honest placeholders — we do not link dead routes or
// fabricate policy pages. No social accounts exist yet, so the social row is
// intentionally omitted rather than faked.
const CONTACT_EMAIL = 'hello@parkingrank.com';

export default function FooterLight() {
  return (
    <footer className="bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-10">
          {/* Brand */}
          <div className="md:col-span-5 space-y-3">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl border-2 border-gray-900">
                <ArrowUpRight className="w-5 h-5" style={{ color: '#17A24A' }} strokeWidth={2.5} />
              </span>
              <span className="text-lg font-bold tracking-tight text-gray-900">ParkingRank</span>
            </Link>
            <p className="text-sm max-w-xs leading-relaxed text-gray-500">
              Independent exit scores for every garage near major venues. Park
              smarter, leave faster.
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              <Mail className="w-4 h-4" style={{ color: '#17A24A' }} />
              {CONTACT_EMAIL}
            </a>
          </div>

          {/* Links */}
          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Directory
              </p>
              <div className="flex flex-col gap-2">
                <Link to="/garages" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Garages</Link>
                <Link to="/events" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Events</Link>
                <Link to="/teams" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Teams</Link>
                <Link to="/cities" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Cities</Link>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                About
              </p>
              <div className="flex flex-col gap-2">
                <Link to="/methodology" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Methodology</Link>
                <Link to="/blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Blog</Link>
                <Link to="/venues" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">Venues</Link>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Legal
              </p>
              <div className="flex flex-col gap-2">
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Privacy%20Policy%20request`}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Privacy
                </a>
                <a
                  href={`mailto:${CONTACT_EMAIL}?subject=Terms%20of%20Use%20request`}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Terms
                </a>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Contact
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">© 2026 ParkingRank. All rights reserved.</p>
          <p className="text-xs text-gray-400">4,700+ garages · 350+ venues · 196 cities</p>
        </div>
      </div>
    </footer>
  );
}
